/*
 * Floating add-to-cart bar for product pages.
 *
 * Visible only while the real .product-form__submit button is outside the
 * viewport. Uses IntersectionObserver against the real button, so it behaves
 * correctly in all three states the design calls for:
 *   - on load, when the button sits below the fold  -> bar shown
 *   - while the button is on screen                 -> bar hidden
 *   - after scrolling past the button               -> bar shown again
 *
 * The bar never owns cart state. Its button forwards the click to the real
 * submit button so variant selection, quantity and the theme's product-form
 * handling stay authoritative.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.target = document.querySelector(
      '.product__info-container .product-form__submit, product-form .product-form__submit'
    );

    // No add-to-cart on the page (e.g. a product with no variants for sale).
    if (!this.button || !this.target) {
      this.remove();
      return;
    }

    this.syncLabel();
    this.button.addEventListener('click', this.onClick.bind(this));

    // A rect check on scroll rather than IntersectionObserver: IO did not
    // report visibility changes reliably for this button on the product page,
    // and this is simple enough to reason about with certainty.
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();

    // The theme re-renders the submit button on variant change, which swaps
    // the label and the disabled state. Mirror both.
    this.mutationObserver = new MutationObserver(this.syncLabel.bind(this));
    this.mutationObserver.observe(this.target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  onScroll() {
    // Deliberately synchronous. An earlier requestAnimationFrame-throttled
    // version was untestable and silently dead whenever frame production was
    // stalled; a single getBoundingClientRect per scroll event is cheap and
    // has no such dependency.
    this.update();
  }

  update() {
    const rect = this.target.getBoundingClientRect();
    // A button only part-way onto the screen is not comfortably tappable, so
    // require it to clear the bottom 80px before the bar steps aside.
    const visible = rect.bottom > 0 && rect.top < window.innerHeight - 80;
    this.hidden = visible;
  }

  onClick() {
    if (this.target.hasAttribute('disabled')) return;
    this.target.click();
  }

  syncLabel() {
    const label = this.target.querySelector('span:not(.sold-out-message)') || this.target;
    const text = label.textContent.trim();
    if (text) this.button.textContent = text;

    if (this.target.hasAttribute('disabled')) {
      this.button.setAttribute('disabled', '');
    } else {
      this.button.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
